import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ChevronDown, MapPin, Trophy, Truck, PenTool, Star } from "lucide-react";
import { api, fmt } from "../lib/api";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { ShareButtons } from "../components/ShareButtons";
import { ELink } from "../components/Editable";
import RichText from "../components/RichText";
import { useLang } from "../i18n/LanguageContext";

// Page d'atterrissage SEO locale : /villes/montreal, /villes/chateauguay…
// Le contenu (titres, descriptions FR/EN, mots-clés) vient de la collection « villes ».
export default function VillePage() {
  const { slug } = useParams();
  const { lang, t, tx } = useLang();
  const [ville, setVille] = useState(null);  // null = chargement, false = introuvable
  const [picks, setPicks] = useState(null);    // 2 bandeaux de 8 choix (4 médailles + 4 trophées)
  const [avis, setAvis] = useState(null);      // avis Google 5 étoiles (affichés sur chaque page ville — 2026-10-23)

  useEffect(() => {
    setVille(null);
    setPicks(null);
    api.get(`/villes/${slug}`).then((r) => setVille(r.data)).catch(() => setVille(false));
  }, [slug]);

  // Avis clients sur chaque page ville — citabilité locale (demande du
  // propriétaire, 2026-10-23) : avis Google 5 étoiles en priorité ; à défaut,
  // les témoignages clients publiés par le propriétaire (section de l'accueil)
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const g = await api.get("/testimonials", { params: { lang } });
        if (!annule && g.data?.source === "google" && g.data.reviews?.length) {
          setAvis({ mode: "google", rating: g.data.rating, items: g.data.reviews });
          return;
        }
      } catch { /* repli témoignages ci-dessous */ }
      try {
        const t = await api.get("/temoignages");
        if (!annule && t.data?.items?.length) {
          setAvis({ mode: "temoignages", items: t.data.items });
          return;
        }
      } catch { /* pas d'avis disponibles : la section reste cachée */ }
      if (!annule) setAvis(null);
    })();
    return () => { annule = true; };
  }, [lang]);

  // 8 choix VRAIMENT aléatoires à chaque visite, adaptés au sport dominant de la
  // ville quand il est défini (ex. hockey, soccer) — images de la boutique seulement
  useEffect(() => {
    if (!ville) return;
    api.get("/products/picks", { params: { sport: ville.sport || "" } })
      .then((r) => setPicks(r.data?.strips || [])).catch(() => setPicks([]));
  }, [ville]);

  if (ville === false) {
    return (
      <div className="pt-40 pb-28 text-center px-6" data-testid="ville-not-found">
        <h1 className="font-serif text-4xl mb-6">{t("category.not_found")}</h1>
        <ELink editPath="link.ville_back" to="/villes" className="text-gold hover:text-champagne transition-colors duration-300 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("villes.back")}
        </ELink>
      </div>
    );
  }
  if (!ville) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  // Bandeau de 8 choix de la boutique (4 médailles + 4 trophées, aléatoires par ville)
  const PickStrip = ({ items, testid }) => !items?.length ? null : (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-8" data-testid={testid}>
      {items.map((p) => (
        <Link key={p.id} to={`/produit/${p.id}`} data-testid={`${testid}-${p.id}`}
          className="bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/50 transition-colors duration-300 group">
          <div className="h-28 bg-white flex items-center justify-center overflow-hidden">
            <img src={p.image} alt={lang === "en" ? (p.name_en || p.name) : p.name} loading="lazy"
              className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" />
          </div>
          <div className="p-3">
            <p className="text-[11px] text-ivory/80 leading-snug line-clamp-2 mb-1">{lang === "en" ? (p.name_en || p.name) : p.name}</p>
            <p className="text-gold text-xs font-bold">{fmt(p.base_price)}</p>
          </div>
        </Link>
      ))}
    </div>
  );

  // Bande des sports cliquable : guide le visiteur vers la page Forfaits avec le
  // sport déjà présélectionné sur les 3 cartes (/forfaits?sport=…)
  const VILLE_SPORTS = ["Hockey", "Soccer", "Football", "Basketball", "Volleyball", "Natation", "Baseball", "Golf", "Danse"]
    .sort((a, b) => a.localeCompare(b, "fr")).concat("Autre");  // alphabétique, « Autre » en fin
  const SportBand = ({ testid }) => {
    const [open, setOpen] = useState(false);
    return (
      <div className="border-y border-gold/15 my-6" data-testid={testid}>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
          className="w-full flex items-center justify-between text-left text-[11px] uppercase tracking-widest py-2.5 text-gold/70 hover:text-gold transition-colors duration-200 cursor-pointer">
          <span className="text-white"><RichText text={tx("villepage.sportband_title", lang === "en" ? "Tournament packages — choose your sport" : "Forfaits tournoi — choisissez votre sport")} path="villepage.sportband_title" /></span>
          <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="pb-2 grid grid-cols-2 sm:grid-cols-5 gap-x-4" data-testid={`${testid}-list`}>
            {VILLE_SPORTS.map((s) => (
              <Link key={s} to={`/forfaits?sport=${encodeURIComponent(s)}`}
                data-testid={`${testid}-pick-${s.toLowerCase()}`}
                className="text-[11px] uppercase tracking-widest py-[7px] text-gold/70 hover:text-gold transition-colors duration-200">
                <span className="text-gold/30 mr-2">•</span>{s}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const title = lang === "en" ? ville.title_en : ville.title_fr;
  const desc = lang === "en" ? ville.desc_en : ville.desc_fr;
  const meta = lang === "en" ? ville.meta_en : ville.meta_fr;
  const region = lang === "en" ? ville.region_en : ville.region_fr;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: lang === "en" ? "By city" : "Par ville", item: "https://mrtrophee.ca/villes" },
      { "@type": "ListItem", position: 3, name: title },
    ],
  };
  // Schéma « service offert dans CETTE ville » (visibilité IA locale, 2026-10-23) :
  // les moteurs d'IA (Gemini surtout, via la recherche Google) savent ainsi, en
  // langage machine, que l'atelier SERT cette ville — clé pour les questions
  // « commerce de trophées à [ville] » quand l'atelier est à Léry
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    url: `https://mrtrophee.ca/villes/${slug}`,
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    serviceType: lang === "en"
      ? "Custom trophies, medals, plaques and engraving"
      : "Trophées, médailles, plaques et gravure personnalisée",
    provider: { "@id": "https://mrtrophee.ca/#entreprise" },
    areaServed: {
      "@type": "City", name: ville.ville,
      containedIn: { "@type": "AdministrativeArea", name: region },
    },
  };

  // FAQ LOCALE par ville (visibilité IA, demande du propriétaire, 2026-10-23) :
  // 3 questions-réponses réelles, visibles sur la page ET déclarées en FAQPage —
  // les IA citent volontiers ces extraits pour « trophées à [ville] »
  const faq = [
    { q: lang === "en" ? `Do you deliver to ${ville.ville}?`
                       : `Livrez-vous à ${ville.ville} ?`,
      a: lang === "en"
        ? `Yes. Every piece is produced at our Léry workshop (Montérégie) and shipped anywhere in Canada, including ${ville.ville} — or you can pick it up free at the workshop.`
        : `Oui. Chaque pièce est produite à notre atelier de Léry (Montérégie) et livrée partout au Canada, dont ${ville.ville} — ou à ramasser sans frais à l'atelier.` },
    { q: lang === "en" ? `How fast can I get trophies in ${ville.ville}?`
                       : `Quels sont les délais pour ${ville.ville} ?`,
      a: lang === "en"
        ? "Production is quick since everything is made in-house. Tell us your “need-by” date when you request a quote (it is a required field) and we commit to it."
        : "La production est rapide puisque tout est fait sur place. Indiquez votre date « livrer avant le » dans votre demande de soumission (champ obligatoire) et nous nous y engageons." },
    { q: lang === "en" ? `Are there team or group discounts in ${ville.ville}?`
                       : `Y a-t-il des rabais pour les équipes et groupes de ${ville.ville} ?`,
      a: lang === "en"
        ? "Yes: volume pricing starts at 6 units, and our turnkey tournament packages bundle medals, trophies and engraving at one clear price per team."
        : "Oui : les prix dégressifs commencent dès 6 unités, et nos forfaits tournoi clé en main regroupent médailles, trophées et gravure à un prix clair par équipe." },
  ];
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-32 pb-28 max-w-7xl mx-auto px-6 lg:px-10" data-testid="ville-page">
      <SEO title={`${title} | Mr Trophée`} description={meta || desc} path={`/villes/${slug}`}
        jsonLd={[breadcrumbLd, serviceLd, faqLd]}
        keywords={(lang === "en" ? ville.seo_keywords_en : ville.seo_keywords) || ville.seo_keywords} />
      <FadeUp>
        <ELink editPath="link.ville_back" to="/villes" data-testid="ville-back"
          className="inline-flex items-center gap-2 text-xs text-ivory/60 hover:text-gold uppercase tracking-widest mb-6 transition-colors duration-300">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("villes.back")}
        </ELink>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          {ville.blason ? (
            <span className="w-8 h-8 rounded-full border border-gold/50 bg-ink overflow-hidden flex items-center justify-center shrink-0">
              <img src={ville.blason} alt={`Armoiries de ${ville.ville}`} className="w-6 h-6 object-contain" data-testid="ville-blason" />
            </span>
          ) : (
            <MapPin className="w-3.5 h-3.5" />
          )}
          {ville.ville} · {region}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="ville-title">{title}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-3xl mb-4" data-testid="ville-intro">{desc}</p>
        {/* Page SPORT → lien retour vers la page de la ville (maillage interne) */}
        {ville.ville_page && (
          <Link to={`/villes/${ville.ville_page}`} data-testid="ville-sport-back"
            className="inline-flex items-center gap-2 text-xs text-gold hover:text-champagne uppercase tracking-widest mb-4 transition-colors duration-200">
            <ArrowLeft className="w-3 h-3" /> {lang === "en" ? `All awards in ${ville.ville}` : `Toutes les récompenses à ${ville.ville}`}
          </Link>
        )}
        {/* Page VILLE → ses pages par sport (SEO « trophées [sport] à [ville] »,
            demande du propriétaire) — maillage interne */}
        {ville.sport_pages?.length > 0 && (
          <div className="mb-4" data-testid="ville-sport-links">
            <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-2">
              {lang === "en" ? `By sport in ${ville.ville}` : `Par sport à ${ville.ville}`}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {ville.sport_pages.map((sp) => (
                <Link key={sp.slug} to={`/villes/${sp.slug}`} data-testid={`ville-sport-link-${sp.slug}`}
                  className="text-[11px] uppercase tracking-widest text-gold/70 hover:text-gold transition-colors duration-200">
                  <span className="text-gold/30 mr-1">•</span>{sp.sport}
                </Link>
              ))}
            </div>
          </div>
        )}
        {/* Page VILLE → ses pages par ÉVÉNEMENT (2e vague SEO, demande du
            propriétaire) : « tournoi de hockey à … », « gala de reconnaissance à … » */}
        {ville.occasion_pages?.length > 0 && (
          <div className="mb-4" data-testid="ville-occasion-links">
            <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-2">
              {lang === "en" ? `By event in ${ville.ville}` : `Par événement à ${ville.ville}`}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {ville.occasion_pages.map((op) => (
                <Link key={op.slug} to={`/villes/${op.slug}`} data-testid={`ville-occasion-link-${op.slug}`}
                  className="text-[11px] uppercase tracking-widest text-gold/70 hover:text-gold transition-colors duration-200">
                  <span className="text-gold/30 mr-1">•</span>{op.occasion}
                </Link>
              ))}
            </div>
          </div>
        )}
        <ShareButtons title={title} label={t("footer.share_page")} />
      </FadeUp>

      {/* 8 choix de la boutique sous le texte d'intro (4 médailles + 4 trophées) */}
      <FadeUp delay={0.04}><PickStrip items={picks?.[0]} testid="ville-picks-a" /></FadeUp>

      {/* Bande des sports → page Forfaits avec le sport présélectionné */}
      <FadeUp delay={0.06}><SportBand testid="ville-sportband-a" /></FadeUp>

      {/* Arguments de vente locaux */}
      <FadeUp delay={0.08}>
        <div className="grid sm:grid-cols-3 gap-4 my-10" data-testid="ville-perks">
          {[
            { Icon: PenTool, fr: "Personnalisation laser", en: "Laser personalization",
              dfr: "Nom, logo et date gravés au laser dans notre atelier de Léry.",
              den: "Name, logo and date laser-engraved in our Léry workshop." },
            { Icon: Truck, fr: `Livraison à ${ville.ville}`, en: `Delivery to ${ville.ville}`,
              dfr: "Expédition rapide partout au Canada — ou ramassage sans frais à l'atelier.",
              den: "Fast shipping across Canada — or free pickup at the workshop." },
            { Icon: Trophy, fr: "Prix dégressifs", en: "Volume discounts",
              dfr: "Rabais par quantité dès 6 unités — idéal tournois, ligues et écoles.",
              den: "Quantity discounts from 6 units — perfect for tournaments, leagues and schools." },
          ].map((p, i) => (
            <div key={i} data-testid={`ville-perk-${i}`}
              className="bg-surface border border-ivory/10 rounded-xl p-5 hover:border-gold/40 transition-colors duration-300">
              <p.Icon className="w-5 h-5 text-gold mb-3" />
              <p className="font-semibold text-sm text-ivory/90 mb-1">{lang === "en" ? p.en : p.fr}</p>
              <p className="text-xs text-ivory/60 leading-relaxed">{lang === "en" ? p.den : p.dfr}</p>
            </div>
          ))}
        </div>

        {/* 8 choix de la boutique sous les arguments (4 médailles + 4 trophées) —
            RETIRÉ : maximum 8 miniatures par page ville (demande client) */}

        {/* Bande des sports → page Forfaits avec le sport présélectionné */}
        <SportBand testid="ville-sportband-b" />

        <div className="flex flex-wrap gap-4 mb-12">
          <ELink editPath="link.ville_forfaits" to="/forfaits" data-testid="ville-cta-forfaits"
            className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-7 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 text-sm">
            <Trophy className="w-4 h-4" /> {t("villes.cta_forfaits")}
          </ELink>
          <ELink editPath="link.ville_contact" to="/contact" data-testid="ville-cta-contact"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-7 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm">
            {t("villes.cta_contact")}
          </ELink>
        </div>
      </FadeUp>

      {/* AVIS CLIENTS sur chaque page ville (demande du propriétaire, 2026-10-23 —
          citabilité locale) : 3 avis Google 5 étoiles RÉELS de l'atelier */}
      {avis?.items?.length > 0 && (
        <FadeUp delay={0.1}>
          <div className="my-10" data-testid="ville-avis">
            <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-3">
              {lang === "en" ? "What our customers say" : "Ce que nos clients disent"}
              {avis.mode === "google" && avis.rating ? (lang === "en"
                ? ` — ${String(avis.rating).replace(".", ",")} ★ on Google`
                : ` — ${String(avis.rating).replace(".", ",")} ★ sur Google`) : ""}
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {avis.items.slice(0, 3).map((r, i) => (
                <figure key={i} data-testid={`ville-avis-${i}`}
                  className="bg-surface border border-ivory/10 rounded-xl p-5 hover:border-gold/40 transition-colors duration-300">
                  {avis.mode === "google" && (
                    <div className="flex gap-0.5 mb-2" aria-label={`${r.rating}/5`}>
                      {[0, 1, 2, 3, 4].map((s) => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "text-gold fill-gold" : "text-ivory/20"}`} />
                      ))}
                    </div>
                  )}
                  <blockquote className="text-xs text-ivory/70 leading-relaxed line-clamp-4 mb-2">
                    {avis.mode === "google" ? r.text : r.texte}
                  </blockquote>
                  <figcaption className="text-[11px] text-gold/80 font-semibold">
                    {avis.mode === "google" ? r.author : r.nom}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </FadeUp>
      )}

      {/* FAQ locale visible (le schéma FAQPage ci-haut reprend exactement ce
          contenu — règle Google : jamais de données structurées invisibles) */}
      <FadeUp delay={0.12}>
        <div className="my-10" data-testid="ville-faq">
          <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-3">
            {lang === "en" ? `Quick answers for ${ville.ville}` : `Réponses rapides pour ${ville.ville}`}
          </p>
          <div className="space-y-2">
            {faq.map((f, i) => (
              <details key={i} data-testid={`ville-faq-${i}`}
                className="group bg-surface border border-ivory/10 rounded-xl px-5 py-4 open:border-gold/40 transition-colors duration-300">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-sm font-semibold text-ivory">
                  {f.q}
                  <ChevronDown className="w-4 h-4 text-gold/70 shrink-0 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <p className="text-sm text-ivory/70 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </FadeUp>

      {/* MAXIMUM 8 miniatures par page ville (demande client, répétée) : la grille
          catalogue de 48 produits est retirée — les 8 choix aléatoires ci-haut
          (4 médailles + 4 trophées) sont les seules miniatures de la page */}
    </div>
  );
}
